import { alpha } from '@mui/material/styles';

import { COLOR_PALETTE } from 'constants/ui';

import {
  NAV_ACCENT_COLOR,
  NAV_ACTIVE_GLOW_ALPHA,
  NAV_ACTIVE_GRADIENT,
  NAV_ACTIVE_ICON_COLOR,
  NAV_ACTIVE_SHEEN_ALPHA,
  NAV_GLASS_BLUR,
} from '../../BottomNav.constants';
import {
  NAV_CARD_BG_ALPHA,
  NAV_CARD_SPRING,
  NAV_SCRIM_ALPHA,
} from '../../BottomNav.groups.constants';

/** Opacity of the card border and tail border (white rim). */
const CARD_BORDER_ALPHA = 0.7;

/** Shadow opacity constants for the card box-shadow layers. */
const CARD_SHADOW_NEAR_ALPHA = 0.08;
const CARD_SHADOW_FAR_ALPHA = 0.22;

/** Active row background gradient alphas (indigo tint). */
const ROW_ACTIVE_BG_ALPHA_START = 0.14;
const ROW_ACTIVE_BG_ALPHA_END = 0.1;

/** Base stagger delay (s) before the first row fades in. */
const ROW_STAGGER_BASE_DELAY = 0.05;

/** Per-row additional delay (s) added per stagger index. */
const ROW_STAGGER_STEP_DELAY = 0.045;

export const rowArrowWrapperStyles = { color: 'text.disabled', display: 'flex' };

/** Full-viewport scrim that dims app content while the card is open. */
export const scrimStyles = {
  position: 'fixed',
  inset: 0,
  zIndex: ({ zIndex }) => zIndex.appBar - 1,
  backgroundColor: ({ palette }) => alpha(palette.common.black, NAV_SCRIM_ALPHA),
  backdropFilter: 'blur(1.5px)',
  WebkitBackdropFilter: 'blur(1.5px)',
};

/**
 * Frosted glass speed-dial card positioned just above the tapped tab.
 *
 * `--tail-offset` CSS custom property controls the tail's horizontal position
 * (set inline as a pixel value from the card's left edge).
 */
const cardStyles = {
  position: 'fixed',
  zIndex: ({ zIndex }) => zIndex.appBar + 1,
  minWidth: '208px',
  transformOrigin: 'bottom center',
  backgroundColor: ({ palette }) => alpha(palette.background.paper, NAV_CARD_BG_ALPHA),
  backdropFilter: NAV_GLASS_BLUR,
  WebkitBackdropFilter: NAV_GLASS_BLUR,
  border: '1px solid',
  borderColor: ({ palette }) => alpha(palette.common.white, CARD_BORDER_ALPHA),
  borderRadius: '20px',
  padding: '8px',
  boxShadow: ({ palette }) =>
    [
      `0px 2px 6px ${alpha(palette.common.black, CARD_SHADOW_NEAR_ALPHA)}`,
      `0px 18px 40px ${alpha(palette.common.black, CARD_SHADOW_FAR_ALPHA)}`,
    ].join(', '),
  animation: `navCardIn 0.34s ${NAV_CARD_SPRING} forwards`,
  '@keyframes navCardIn': {
    from: {
      opacity: 0,
      transform: 'translateY(14px) scale(0.9)',
    },
    to: {
      opacity: 1,
      transform: 'translateY(0) scale(1)',
    },
  },
  /**
   * Downward-pointing tail anchored by --tail-offset.
   */
  '&::after': {
    content: '""',
    position: 'absolute',
    bottom: '-7px',
    left: 'var(--tail-offset, 50%)',
    transform: 'translateX(-50%) rotate(45deg)',
    width: '14px',
    height: '14px',
    backgroundColor: ({ palette }) => alpha(palette.background.paper, NAV_CARD_BG_ALPHA),
    borderRight: '1px solid',
    borderBottom: '1px solid',
    borderColor: ({ palette }) => alpha(palette.common.white, CARD_BORDER_ALPHA),
    borderRadius: '0 0 4px 0',
  },
};

/**
 * Returns the card positioning sx object merged with the base card styles.
 * Call site stores the result in useMemo to avoid a new array/object on every render.
 *
 * @param {number} anchorLeft - px from viewport left
 * @param {number} bottomOffset - px from viewport bottom
 * @param {number} tailOffset - px from card left for the downward tail
 * @returns {Array}
 */
export const getCardSx = (anchorLeft, bottomOffset, tailOffset) => [
  cardStyles,
  {
    '--tail-offset': `${tailOffset}px`,
    bottom: `${bottomOffset}px`,
    left: `${anchorLeft}px`,
  },
];

/** Uppercase, tracked, muted group header inside the card. */
export const cardHeadStyles = {
  padding: '8px 12px 6px',
};

/** A single member row inside the card. */
const cardRowStyles = {
  display: 'flex',
  alignItems: 'center',
  gap: '11px',
  padding: '9px 11px',
  borderRadius: '14px',
  cursor: 'pointer',
  border: 'none',
  background: 'transparent',
  width: '100%',
  textAlign: 'left',
  WebkitTapHighlightColor: 'transparent',
  animation: `navRowIn 0.3s ease forwards`,
  opacity: 0,
  transform: 'translateY(8px)',
  '@keyframes navRowIn': {
    to: {
      opacity: 1,
      transform: 'translateY(0)',
    },
  },
  '&:active': {
    backgroundColor: COLOR_PALETTE.PURPLE[2],
  },
};

/** Active/current member row gets an indigo-tint gradient background. */
const cardRowActiveStyles = {
  background: `linear-gradient(135deg, ${alpha(
    COLOR_PALETTE.PURPLE[6],
    ROW_ACTIVE_BG_ALPHA_START,
  )}, ${alpha(COLOR_PALETTE.PURPLE[8], ROW_ACTIVE_BG_ALPHA_END)})`,
};

/**
 * Returns the row sx array for the given stagger index and active state.
 * Call site stores this in useMemo.
 *
 * @param {boolean} isActive
 * @param {number} animationIndex
 * @returns {Array}
 */
export const getCardRowSx = (isActive, animationIndex) => [
  cardRowStyles,
  isActive ? cardRowActiveStyles : null,
  { animationDelay: `${ROW_STAGGER_BASE_DELAY + animationIndex * ROW_STAGGER_STEP_DELAY}s` },
];

/** Icon chip inside each card row — plain indigo tint by default. */
const cardChipStyles = {
  width: '34px',
  height: '34px',
  borderRadius: '10px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  backgroundColor: COLOR_PALETTE.PURPLE[2],
  color: NAV_ACCENT_COLOR,
  flexShrink: 0,
};

/** Active chip uses the same gradient pill as the active tab icon. */
const cardChipActiveStyles = {
  background: NAV_ACTIVE_GRADIENT,
  color: NAV_ACTIVE_ICON_COLOR,
  boxShadow: `0px 4px 12px ${alpha(NAV_ACCENT_COLOR, NAV_ACTIVE_GLOW_ALPHA)}`,
  '&::after': {
    boxShadow: `inset 0px 1px 1px ${alpha('#ffffff', NAV_ACTIVE_SHEEN_ALPHA)}`,
  },
};

/**
 * Returns the chip sx array for the given active state.
 * Call site stores this in useMemo.
 *
 * @param {boolean} isActive
 * @returns {Array}
 */
export const getCardChipSx = isActive => [cardChipStyles, isActive ? cardChipActiveStyles : null];

/** Row text column (label + optional subtitle). */
export const cardRowTextStyles = {
  flex: 1,
  minWidth: 0,
};

/** Primary label in a card row. */
export const cardRowLabelStyles = {
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
};

/** Label color for the active row. */
export const cardRowLabelActiveColor = COLOR_PALETTE.PURPLE[8];

/** Right-side area (badge + chevron). */
export const cardRowRightStyles = {
  marginLeft: 'auto',
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  flexShrink: 0,
};

/** Unread count pill shown on the right side of a card row. */
export const miniBadgeStyles = {
  backgroundColor: ({ palette }) => palette.error.main,
  borderRadius: '999px',
  color: ({ palette }) => palette.common.white,
  fontSize: '10px',
  fontWeight: 800,
  lineHeight: 1.4,
  padding: '1px 6px',
};
